import { useFormikContext } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { DatePicker, Select, Textarea } from '@loadsmart/loadsmart-ui'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import {
  Button,
  Dialog,
  Field,
  Layout,
  Text,
  TextField,
  ToggleGroup,
  type ToggleGroupChangeEventDetail,
} from '@loadsmart/miranda-react'

import {
  AppointmentLoadingTypeOptions,
  AppointmentModeOptions,
  AppointmentTimeOptions,
  type StopAppointment,
} from 'core/appointments/domain/Appointment'
import { RequestStatus } from 'core/domain/Request'
import { usePasteFromClipboard } from 'core/ui/hooks/usePasteFromClipboard'

import { hasAppointmentReason } from '../../domain/Appointment'
import { type AppointmentFormValues } from '../../infra/ShipmentRepository'
import { APPOINTMENT_MISSCHEDULING_REASON_OPTIONS } from '../../presenters/appointment'
import { formatDialogTitle } from '../../presenters/appointmentPresenter'
import { useUpdateAppointment } from '../hooks/useUpdateAppointment'

export const APPOINTMENT_TIME_SELECT_OPTIONS = [
  { label: 'Specific time', value: AppointmentTimeOptions.At },
  { label: 'Between', value: AppointmentTimeOptions.Between },
]

export const APPOINTMENT_MODE_OPTIONS = [
  {
    label: 'Appointment',
    value: AppointmentModeOptions.Appt,
  },
  {
    label: 'FCFS',
    value: AppointmentModeOptions.Fcfs,
  },
]

export const APPOINTMENT_LOADING_TYPE_OPTIONS = [
  {
    label: 'Live',
    value: AppointmentLoadingTypeOptions.Live,
  },
  {
    label: 'Drop',
    value: AppointmentLoadingTypeOptions.Drop,
  },
]

const FullSizeDatePicker = styled.div`
  width: 100%;

  [role='menubar'] > div > div > div {
    width: 100%;
  }
`

type AppointmentDialogFormProps = {
  shipmentUUID: string
  stopIndex: number
  appointment?: StopAppointment
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

export function AppointmentDialogForm({
  shipmentUUID,
  stopIndex,
  appointment,
  isOpen,
  onClose,
  onConfirm,
}: AppointmentDialogFormProps) {
  const { setFieldValue, handleChange, values, errors, isSubmitting, isValid } =
    useFormikContext<AppointmentFormValues>()

  const [showAppointmentReason, setShowAppointmentReason] = useState(
    hasAppointmentReason(values, appointment)
  )

  const { update: updateAppointment, requestStatus } = useUpdateAppointment()

  const { paste } = usePasteFromClipboard({
    onSuccess: pastedText => {
      setFieldValue('confirmationNumber', pastedText)
      toast.success('Confirmation number pasted!')
    },
  })

  async function onSubmit() {
    await updateAppointment({
      shipmentUUID,
      stopIndex,
      data: values,
    })
  }

  const handlePasteText = useCallback(async () => {
    await paste()
  }, [paste])

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      onConfirm()
      toast.success('Appointment updated successfully')
    }
  }, [
    onConfirm,
    requestStatus,
    values.confirmationNumber,
    values.loadingType,
    values.misschedulingNote,
    values.misschedulingReason.value,
    values.mode,
    values.time,
    values.timeEnd,
    values.timeType?.value,
  ])

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Could not update appointment, try again later')
    }
  }, [requestStatus])

  function handleToggleChange(event: CustomEvent<ToggleGroupChangeEventDetail>, fieldName: string) {
    setFieldValue(fieldName, event.detail.value)
  }

  function handleDateChange(event: EventLike<string | null>) {
    handleChange(event)
    const date = event.target.value || ''
    const changed = hasAppointmentReason({ ...values, date }, appointment)
    setShowAppointmentReason(changed)
  }

  function handleTimeChange(event: Event, fieldName: 'time' | 'timeEnd') {
    handleChange(event)
    const time = (event.target as HTMLInputElement).value
    const changed = hasAppointmentReason({ ...values, [fieldName]: time }, appointment)
    setShowAppointmentReason(changed)
  }

  return (
    <Dialog open={isOpen} size="small" onClose={onClose} style={{ marginTop: 0 }}>
      <Dialog.Header>{formatDialogTitle(appointment)}</Dialog.Header>
      <Dialog.Body>
        <Layout.Stack>
          <Layout.Stack gap="spacing-1">
            <Text variant="body-md" color="color-text-tertiary">
              <label htmlFor="date">Appointment date</label>
            </Text>
            <FullSizeDatePicker>
              <DatePicker id="date" name="date" value={values.date} onChange={handleDateChange} />
            </FullSizeDatePicker>
          </Layout.Stack>
          <Layout.Group>
            <Layout.Stack gap="spacing-1">
              <Text variant="body-md" color="color-text-tertiary">
                Type
              </Text>
              <ToggleGroup
                id="mode"
                type="single-strict"
                onChange={(event: CustomEvent<ToggleGroupChangeEventDetail>) =>
                  handleToggleChange(event, 'mode')
                }
                value={values.mode}
                options={APPOINTMENT_MODE_OPTIONS}
              />
            </Layout.Stack>
            <Layout.Stack gap="spacing-1">
              <Text variant="body-md" color="color-text-tertiary">
                Loading type
              </Text>
              <ToggleGroup
                id="loadingType"
                type="single-strict"
                onChange={(event: CustomEvent<ToggleGroupChangeEventDetail>) =>
                  handleToggleChange(event, 'loadingType')
                }
                value={values.loadingType}
                options={APPOINTMENT_LOADING_TYPE_OPTIONS}
              />
            </Layout.Stack>
          </Layout.Group>
          <Layout.Stack gap="spacing-1">
            <Text variant="body-md" color="color-text-tertiary">
              Time
            </Text>
            <Layout.Grid gap="spacing-1" minColumnWidth="30%">
              <Select
                name="timeType"
                options={APPOINTMENT_TIME_SELECT_OPTIONS}
                value={values.timeType}
                onChange={handleChange}
                hideClear
              />
              <TextField
                id="time"
                type="text"
                pattern="[0-9]{4}"
                max-length={4}
                placeholder="e.g. 1300"
                aria-label="time"
                value={values.time}
                onInput={event => handleTimeChange(event, 'time')}
              />
              {values.timeType?.value === 'between' && (
                <TextField
                  id="timeEnd"
                  type="text"
                  pattern="[0-9]{4}"
                  max-length={4}
                  placeholder="e.g. 1300"
                  aria-label="timeEnd"
                  value={values.timeEnd}
                  onInput={event => handleTimeChange(event, 'timeEnd')}
                />
              )}
            </Layout.Grid>
            {errors && (
              <Text variant="body-sm" color="color-danger-100">
                {errors.timeEnd || errors.time || errors.timeType}
              </Text>
            )}
          </Layout.Stack>
          <Layout.Stack gap="spacing-1">
            <Text variant="body-md" color="color-text-tertiary">
              Confirmation number (optional)
            </Text>
            <Layout.Group align="center" justify="space-between">
              <TextField
                id="confirmationNumber"
                aria-label="confirmationNumber"
                value={values.confirmationNumber ?? ''}
                onInput={handleChange}
                style={{ flex: 1 }}
              />
              <Button type="button" onClick={handlePasteText} variant="tertiary" size="small">
                Paste code
              </Button>
            </Layout.Group>
          </Layout.Stack>
          {appointment && showAppointmentReason && (
            <Layout.Stack gap="spacing-1">
              {appointment.status === 'pending' && (
                <Text variant="body-sm" color="color-warning-100">
                  Selected time or date falls outside the client's suggested schedule. Please,
                  provide a reason for consideration.
                </Text>
              )}
              <Field required>
                <Field.Label color="color-text-tertiary">Reschedule reason</Field.Label>
                <Select
                  name="misschedulingReason"
                  placeholder="Select reason"
                  options={APPOINTMENT_MISSCHEDULING_REASON_OPTIONS}
                  value={values.misschedulingReason}
                  onChange={handleChange}
                  hideClear
                />
              </Field>
              <Field
                label="Note (max 100 characters)"
                required={values.misschedulingReason.value === 'other'}
              >
                <Textarea
                  onInput={handleChange}
                  name="misschedulingNote"
                  value={values.misschedulingNote}
                  aria-label="note"
                />
              </Field>
            </Layout.Stack>
          )}
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary onClick={onClose}>Cancel</Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          className="alert"
          onClick={onSubmit}
          loading={requestStatus === RequestStatus.Pending}
          disabled={!isValid || isSubmitting}
        >
          Confirm
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
